import { useMemo, useRef } from 'react';
import { buildBridgePreamble } from './bridge';
import { type ArtifactRpcFn, useArtifactBridge } from './useArtifactBridge';
import { stripFence } from './utils';

interface Props {
  /** 단일 self-contained HTML 문서 (LLM 생성). 부모 origin 과 격리된 iframe 에서 실행. */
  html: string;
  /** iframe 내부에서 `window.artifact.*` RPC 를 부모로 라우팅하기 위한 artifact id. */
  artifactId: string;
  /** 런타임에 runSavedQuery 가 만료된 queryId 로 호출됐을 때 상위 패널에 알림. */
  onQueryExpired?: (queryId: string | null) => void;
  /** RPC 라우팅 오버라이드 (관리자 검토 패널). 미지정 시 사용자 경로. */
  rpc?: ArtifactRpcFn;
  /** 채팅 대시보드 수동 편집 영속화용 — 현재 표시 버전 번호. */
  dashboardVersion?: number;
}

// iframe 내부의 기본 스타일 + Chart.js 무한 확장 방지 방어막.
// LLM 이 차트 컨테이너에 고정 높이를 안 주면 Chart.js 의 responsive+maintainAspectRatio:false
// 조합이 리사이즈 피드백 루프를 만들어 대시보드 세로 길이가 무한히 늘어난다. 다음 CSS 로 강제 상한:
//   - canvas: 480px 이상 못 커짐 (!important 로 인라인 오버라이드)
//   - chart-* 클래스 컨테이너: 600px 상한 + overflow 숨김
const DEFAULT_WRAPPER_HEAD =
  '<!doctype html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"><style>html,body{margin:0;padding:0;font-family:system-ui,-apple-system,"Segoe UI",sans-serif;background:#fff}canvas{max-height:480px!important}[class*="chart"]{max-height:600px;overflow:hidden}</style></head><body>';
const DEFAULT_WRAPPER_TAIL = '</body></html>';

/**
 * iframe sandbox + srcDoc 로 LLM 생성 HTML 을 안전하게 렌더.
 * - `allow-scripts` 만 부여, `allow-same-origin` 은 절대 X — 부모 도메인 쿠키/Storage 차단.
 * - srcDoc 맨 앞에 bridge preamble 을 주입해 iframe 안의 사용자 코드가 `window.artifact.query(...)`
 *   등으로 기존 skill 인프라 (db.run_query, excel.query 등) 를 호출할 수 있도록 한다.
 * - markdown fence (```html ... ```) / `<html>`·`<body>` 없는 fragment 는 자동 정규화.
 *
 * 차트 등 라이브러리가 필요하면 LLM 이 `<script src="/artifact-runtime/chart.umd.js">` 를 넣도록
 * system prompt 가 안내. iframe sandbox 는 외부 `<script src>` 로딩은 허용하므로 우리 서버가
 * 정적 자산으로 서빙한 Chart.js 는 origin 무관 로드 가능.
 */
export function HtmlArtifact({ html, artifactId, onQueryExpired, rpc, dashboardVersion }: Props) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  useArtifactBridge(iframeRef, artifactId, { onQueryExpired, rpc, dashboardVersion });

  const doc = useMemo(() => {
    const stripped = stripFence(html, 'html').trim();
    const head = stripped.slice(0, 64).toLowerCase();
    const bridge = buildBridgePreamble();
    if (head.startsWith('<!doctype') || head.startsWith('<html')) {
      // <html> / <!doctype> 가 있는 완전한 문서면 <head> 안 or 시작 직후에 bridge 삽입.
      // 간단히: 가장 먼저 등장하는 `<head>` 태그 바로 뒤에 삽입. 없으면 doctype/ <html ...> 뒤.
      const headMatch = stripped.match(/<head[^>]*>/i);
      if (headMatch && headMatch.index !== undefined) {
        const insertAt = headMatch.index + headMatch[0].length;
        return stripped.slice(0, insertAt) + bridge + stripped.slice(insertAt);
      }
      const htmlMatch = stripped.match(/<html[^>]*>/i);
      if (htmlMatch && htmlMatch.index !== undefined) {
        const insertAt = htmlMatch.index + htmlMatch[0].length;
        return stripped.slice(0, insertAt) + bridge + stripped.slice(insertAt);
      }
      return bridge + stripped;
    }
    return `${DEFAULT_WRAPPER_HEAD}${bridge}${stripped}${DEFAULT_WRAPPER_TAIL}`;
  }, [html]);

  return (
    <iframe
      ref={iframeRef}
      title="artifact"
      sandbox="allow-scripts allow-modals"
      srcDoc={doc}
      className="block h-full w-full border-0 bg-white"
    />
  );
}
